import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { usePwaInstall } from "@/hooks/usePwaInstall";
import { Download, Share, PlusSquare, X, Smartphone } from "lucide-react";

const DISMISS_KEY = "rs_pwa_install_dismissed";

function IosSteps() {
  return (
    <ol className="space-y-3 text-sm text-muted-foreground mt-2">
      <li className="flex items-start gap-2.5">
        <span className="mt-0.5 w-5 h-5 rounded-full bg-primary/15 text-primary text-xs flex items-center justify-center shrink-0 font-mono-num">1</span>
        <span>Tap the <Share className="w-4 h-4 inline text-primary -mt-0.5" /> <b className="text-foreground">Share</b> icon in Safari's toolbar.</span>
      </li>
      <li className="flex items-start gap-2.5">
        <span className="mt-0.5 w-5 h-5 rounded-full bg-primary/15 text-primary text-xs flex items-center justify-center shrink-0 font-mono-num">2</span>
        <span>Scroll down and choose <PlusSquare className="w-4 h-4 inline text-primary -mt-0.5" /> <b className="text-foreground">Add to Home Screen</b>.</span>
      </li>
      <li className="flex items-start gap-2.5">
        <span className="mt-0.5 w-5 h-5 rounded-full bg-primary/15 text-primary text-xs flex items-center justify-center shrink-0 font-mono-num">3</span>
        <span>Tap <b className="text-foreground">Add</b> — Reefer Sathi appears on your home screen.</span>
      </li>
    </ol>
  );
}

export function InstallButton({ className = "", onDone }) {
  const { canInstall, isIOS, isStandalone, promptInstall } = usePwaInstall();
  const [iosOpen, setIosOpen] = useState(false);
  if (isStandalone || (!canInstall && !isIOS)) return null;

  const handle = async () => {
    if (isIOS) { setIosOpen(true); return; }
    await promptInstall();
    onDone?.();
  };

  return (
    <>
      <button
        data-testid="install-app-btn"
        onClick={handle}
        className={`flex items-center gap-2 px-2 py-2 text-xs text-muted-foreground hover:text-primary transition-colors ${className}`}
      >
        <Download className="w-4 h-4" /> Install app
      </button>
      <IosDialog open={iosOpen} onOpenChange={setIosOpen} />
    </>
  );
}

function IosDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border" data-testid="ios-install-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-primary" /> Add to Home Screen
          </DialogTitle>
          <DialogDescription>Install Reefer Sathi on your iPhone or iPad for one-tap access.</DialogDescription>
        </DialogHeader>
        <IosSteps />
      </DialogContent>
    </Dialog>
  );
}

export function InstallBanner() {
  const { canInstall, isIOS, isStandalone, promptInstall } = usePwaInstall();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });
  const [iosOpen, setIosOpen] = useState(false);

  const show = !isStandalone && !dismissed && (canInstall || isIOS);
  if (!show) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch {}
    setDismissed(true);
  };

  const handle = async () => {
    if (isIOS) { setIosOpen(true); return; }
    const ok = await promptInstall();
    if (ok) dismiss();
  };

  return (
    <>
      <div
        data-testid="install-banner"
        style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
        className="fixed inset-x-3 z-40 mx-auto max-w-md rounded-xl border border-primary/30 bg-card/95 backdrop-blur shadow-2xl px-4 py-3 flex items-center gap-3 animate-fadeup"
      >
        <div className="w-9 h-9 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0">
          <Download className="w-4.5 h-4.5 text-primary" style={{ width: 18, height: 18 }} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight">Install Reefer Sathi</p>
          <p className="text-xs text-muted-foreground leading-tight mt-0.5">Add it to your home screen for quick access.</p>
        </div>
        <Button data-testid="install-banner-btn" size="sm" onClick={handle} className="rounded-full h-8 text-xs bg-primary text-primary-foreground hover:bg-primary/90 shrink-0">
          Install
        </Button>
        <button data-testid="install-banner-dismiss" onClick={dismiss} className="text-muted-foreground hover:text-foreground transition-colors shrink-0 p-1">
          <X className="w-4 h-4" />
        </button>
      </div>
      <IosDialog open={iosOpen} onOpenChange={setIosOpen} />
    </>
  );
}
